// ============================================================
// Shrine of Boons — buff/curse pool + active-boon aggregation
// ============================================================
//
// The Shrine of Boons zone-clear node offers 3 random rolls from this 16-entry
// pool. The player picks one and it becomes an "active boon" for the rest of
// the run. Max 2 active at a time — picking a 3rd replaces the oldest.
//
// Each boon has a positive and a negative effect. Some entries are "Blessing"
// tier (mild trade), others are "Curse" tier (big upside, heavy downside).
//
// Effect wiring lives in BattleScene / GameStateManager: this file just stores
// the data + an aggregator that flattens the active-boon list into a single
// struct combat/loot paths can query.
export const BOON_POOL = [
    // ── Blessings (9) ─────────────────────────────────────────────────────────
    {
        id: 'blessing_fire',
        name: 'Blessing of Fire',
        tier: 'blessing',
        description: '+20% fire resist · −30% frost resist',
        iconKey: 'icon_boon_17', // red/fire gem icon
        effects: { fireResistBonus: 20, frostResistBonus: -30 },
    },
    {
        id: 'blessing_frost',
        name: 'Blessing of Frost',
        tier: 'blessing',
        description: '+20% frost resist · −30% fire resist',
        iconKey: 'icon_boon_2',
        effects: { frostResistBonus: 20, fireResistBonus: -30 },
    },
    {
        id: 'blessing_nature',
        name: 'Blessing of Nature',
        tier: 'blessing',
        description: '+20% nature resist · −30% shadow resist',
        iconKey: 'icon_boon_4', // green gem icon (formerly on Shadow)
        effects: { natureResistBonus: 20, shadowResistBonus: -30 },
    },
    {
        id: 'blessing_shadow',
        name: 'Blessing of Shadow',
        tier: 'blessing',
        description: '+20% shadow resist · −30% nature resist',
        iconKey: 'icon_boon_14', // dark/purple gem (formerly on Cursed Greed)
        effects: { shadowResistBonus: 20, natureResistBonus: -30 },
    },
    {
        id: 'hunters_resolve',
        name: "Hunter's Resolve",
        tier: 'blessing',
        description: '+45% crit damage · −10% max HP',
        iconKey: 'icon_boon_5',
        effects: { critDamagePct: 0.45, maxHpPct: -0.10 },
    },
    {
        id: 'casters_covenant',
        name: "Caster's Covenant",
        tier: 'blessing',
        description: '+50% spell power · −30% auto-attack damage',
        iconKey: 'icon_boon_6',
        effects: { spellPowerPct: 0.50, autoAttackPct: -0.30 },
    },
    {
        id: 'swift_shadow',
        name: 'Swift Shadow',
        tier: 'blessing',
        description: '+20% lifesteal · −20% max HP',
        iconKey: 'icon_boon_7',
        effects: { lifestealPct: 0.20, maxHpPct: -0.20 },
    },
    {
        id: 'slow_and_steady',
        name: 'Slow and Steady',
        tier: 'blessing',
        description: '+30% max HP · −15% atk speed',
        iconKey: 'icon_boon_8',
        effects: { maxHpPct: 0.30, attackSpeedPct: -0.15 },
    },
    {
        id: 'inner_flame',
        name: 'Inner Flame',
        tier: 'blessing',
        description: '+1% SP per kill (cap 100) · base mana −50%',
        iconKey: 'icon_boon_9',
        effects: {
            stackPerKill: { stat: 'spellPowerPct', perStack: 0.01, cap: 100 },
            maxMpPct: -0.50,
        },
    },
    // ── Curses (7) ────────────────────────────────────────────────────────────
    {
        id: 'merchants_gamble',
        name: "Merchant's Gamble",
        tier: 'curse',
        description: '×3 gold gain · relic drops −50%',
        iconKey: 'icon_boon_10',
        effects: { goldMult: 3, relicDropMult: 0.5 },
    },
    {
        id: 'reapers_pact',
        name: "Reaper's Pact",
        tier: 'curse',
        description: '+55% dmg per dead hero · heroes can\'t be resurrected',
        iconKey: 'icon_boon_11',
        effects: { damagePerDeadHeroPct: 0.55, cannotResurrect: true },
    },
    {
        id: 'make_it_easy',
        name: 'Make it Easy',
        tier: 'curse',
        description: '+60% damage · −50% DEF',
        iconKey: 'icon_boon_12',
        effects: { damagePct: 0.60, defPct: -0.50 },
    },
    {
        id: 'lucky_break',
        name: 'Lucky Break',
        tier: 'curse',
        description: '+40% loot chance · can\'t recycle items',
        iconKey: 'icon_boon_13',
        effects: { lootChancePct: 0.40, cannotRecycle: true },
    },
    {
        id: 'cursed_greed',
        name: 'Cursed Greed',
        tier: 'curse',
        description: 'All drops roll +1 rarity (caps at legendary) · bosses +30% HP',
        iconKey: 'icon_boon_27',
        effects: { rarityBumpChance: 1, bossHpMult: 1.3 },
    },
    {
        id: 'blood_price',
        name: 'Blood Price',
        tier: 'curse',
        description: 'Heroes heal 50% less · +1% damage per kill (cap 50)',
        iconKey: 'icon_boon_15',
        effects: {
            healReceivedPct: -0.50,
            stackPerKill: { stat: 'damagePct', perStack: 0.01, cap: 50 },
        },
    },
    {
        id: 'no_turning_back',
        name: 'No Turning Back',
        tier: 'curse',
        description: '×3 gold gain · Stay button disabled for the run',
        iconKey: 'icon_boon_16',
        effects: { goldMult: 3, cannotStay: true },
    },
];
/** Lookup helper — returns the full def or undefined. */
export function getBoonDef(id) {
    return BOON_POOL.find((b) => b.id === id);
}
export function aggregateBoonEffects(boons) {
    const out = {
        fireResistBonus: 0, frostResistBonus: 0, shadowResistBonus: 0, natureResistBonus: 0,
        critDamagePct: 0, maxHpPct: 0, spellPowerPct: 0, autoAttackPct: 0,
        lifestealPct: 0, attackSpeedPct: 0, damagePct: 0, defPct: 0, maxMpPct: 0,
        goldMult: 1, lootChancePct: 0, relicDropMult: 1, bossHpMult: 1,
        healReceivedPct: 0, damagePerDeadHeroPct: 0, rarityBumpChance: 0,
        cannotResurrect: false, cannotRecycle: false, cannotStay: false,
        hasAny: false,
    };
    if (!boons || boons.length === 0)
        return out;
    out.hasAny = true;
    for (const ab of boons) {
        // Lookup falls through to altar reward pool if the id isn't a shrine boon —
        // both share the same effect shape so the downstream math is identical.
        const def = getBoonDef(ab.id) ?? getAltarRewardDef(ab.id);
        if (!def)
            continue;
        const e = def.effects;
        if (e.fireResistBonus)
            out.fireResistBonus += e.fireResistBonus;
        if (e.frostResistBonus)
            out.frostResistBonus += e.frostResistBonus;
        if (e.shadowResistBonus)
            out.shadowResistBonus += e.shadowResistBonus;
        if (e.natureResistBonus)
            out.natureResistBonus += e.natureResistBonus;
        if (e.critDamagePct)
            out.critDamagePct += e.critDamagePct;
        if (e.maxHpPct)
            out.maxHpPct += e.maxHpPct;
        if (e.spellPowerPct)
            out.spellPowerPct += e.spellPowerPct;
        if (e.autoAttackPct)
            out.autoAttackPct += e.autoAttackPct;
        if (e.lifestealPct)
            out.lifestealPct += e.lifestealPct;
        if (e.attackSpeedPct)
            out.attackSpeedPct += e.attackSpeedPct;
        if (e.damagePct)
            out.damagePct += e.damagePct;
        if (e.defPct)
            out.defPct += e.defPct;
        if (e.maxMpPct)
            out.maxMpPct += e.maxMpPct;
        if (e.goldMult)
            out.goldMult *= e.goldMult; // multiplicative stacking
        if (e.lootChancePct)
            out.lootChancePct += e.lootChancePct;
        if (e.relicDropMult)
            out.relicDropMult *= e.relicDropMult; // multiplicative
        if (e.bossHpMult)
            out.bossHpMult *= e.bossHpMult; // multiplicative
        if (e.healReceivedPct)
            out.healReceivedPct += e.healReceivedPct;
        if (e.damagePerDeadHeroPct)
            out.damagePerDeadHeroPct += e.damagePerDeadHeroPct;
        if (e.rarityBumpChance)
            out.rarityBumpChance = Math.max(out.rarityBumpChance, e.rarityBumpChance);
        if (e.cannotResurrect)
            out.cannotResurrect = true;
        if (e.cannotRecycle)
            out.cannotRecycle = true;
        if (e.cannotStay)
            out.cannotStay = true;
        // Stacking boons contribute via their runtime stack counter
        if (e.stackPerKill) {
            const stacks = Math.min(e.stackPerKill.cap, ab.stacks ?? 0);
            if (e.stackPerKill.stat === 'spellPowerPct')
                out.spellPowerPct += stacks * e.stackPerKill.perStack;
            else if (e.stackPerKill.stat === 'damagePct')
                out.damagePct += stacks * e.stackPerKill.perStack;
        }
    }
    return out;
}
/** Picks 3 distinct random boons for a shrine visit. Fewer than 3 returned if
 *  the pool ever shrinks below 3 (shouldn't happen in normal play). */
export function rollShrineOffering(count = 3) {
    const shuffled = [...BOON_POOL].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
}
/** Add a boon to the player's active list. Enforces max cap by evicting the
 *  oldest boon (FIFO). Returns the evicted boon if one was removed. */
export function applyBoon(activeBoons, boonId, maxActive = 2) {
    // If already active, refresh (reset stacks if stacking) but keep slot.
    const existing = activeBoons.findIndex((b) => b.id === boonId);
    if (existing !== -1) {
        activeBoons[existing] = { id: boonId, stacks: 0 };
        return {};
    }
    let evicted;
    if (activeBoons.length >= maxActive) {
        evicted = activeBoons.shift();
    }
    activeBoons.push({ id: boonId, stacks: 0 });
    return { evicted };
}
/** Tier pools — Modest/Devout/Grand. Entries pure upside so the gold is the
 *  cost. Icons reuse the shrine icon keys. */
export const ALTAR_POOLS = {
    modest: [
        { id: 'altar_modest_hp', name: 'Lesser Vigor', description: '+8% max HP for the run', iconKey: 'icon_boon_8', effects: { maxHpPct: 0.08 } },
        { id: 'altar_modest_atk', name: 'Lesser Might', description: '+8% damage for the run', iconKey: 'icon_boon_12', effects: { damagePct: 0.08 } },
        { id: 'altar_modest_sp', name: 'Lesser Focus', description: '+12% spell power', iconKey: 'icon_boon_6', effects: { spellPowerPct: 0.12 } },
        { id: 'altar_modest_crit', name: 'Lesser Precision', description: '+12% crit damage', iconKey: 'icon_boon_5', effects: { critDamagePct: 0.12 } },
        { id: 'altar_modest_atkspd', name: 'Lesser Swiftness', description: '+5% attack speed', iconKey: 'icon_boon_7', effects: { attackSpeedPct: 0.05 } },
        { id: 'altar_modest_def', name: 'Lesser Fortitude', description: '+15% DEF', iconKey: 'icon_boon_4', effects: { defPct: 0.15 } },
        { id: 'altar_modest_gold', name: 'Lesser Greed', description: '×1.3 gold gain', iconKey: 'icon_boon_10', effects: { goldMult: 1.3 } },
    ],
    devout: [
        { id: 'altar_devout_hp', name: 'Greater Vigor', description: '+25% max HP for the run', iconKey: 'icon_boon_8', effects: { maxHpPct: 0.25 } },
        { id: 'altar_devout_atk', name: 'Greater Might', description: '+20% damage for the run', iconKey: 'icon_boon_12', effects: { damagePct: 0.20 } },
        { id: 'altar_devout_sp', name: 'Greater Focus', description: '+30% spell power', iconKey: 'icon_boon_6', effects: { spellPowerPct: 0.30 } },
        { id: 'altar_devout_crit', name: 'Greater Precision', description: '+30% crit damage', iconKey: 'icon_boon_5', effects: { critDamagePct: 0.30 } },
        { id: 'altar_devout_atkspd', name: 'Greater Swiftness', description: '+12% attack speed', iconKey: 'icon_boon_7', effects: { attackSpeedPct: 0.12 } },
        { id: 'altar_devout_def', name: 'Greater Fortitude', description: '+40% DEF', iconKey: 'icon_boon_4', effects: { defPct: 0.40 } },
        { id: 'altar_devout_gold', name: 'Greater Greed', description: '×2 gold gain', iconKey: 'icon_boon_10', effects: { goldMult: 2 } },
        { id: 'altar_devout_loot', name: "Devout's Luck", description: '+25% loot chance', iconKey: 'icon_boon_13', effects: { lootChancePct: 0.25 } },
    ],
    grand: [
        { id: 'altar_grand_hp', name: 'Divine Vigor', description: '+60% max HP for the run', iconKey: 'icon_boon_8', effects: { maxHpPct: 0.60 } },
        { id: 'altar_grand_atk', name: 'Divine Might', description: '+50% damage for the run', iconKey: 'icon_boon_12', effects: { damagePct: 0.50 } },
        { id: 'altar_grand_sp', name: 'Divine Focus', description: '+75% spell power', iconKey: 'icon_boon_6', effects: { spellPowerPct: 0.75 } },
        { id: 'altar_grand_crit', name: 'Divine Precision', description: '+75% crit damage', iconKey: 'icon_boon_5', effects: { critDamagePct: 0.75 } },
        { id: 'altar_grand_atkspd', name: 'Divine Swiftness', description: '+25% attack speed', iconKey: 'icon_boon_7', effects: { attackSpeedPct: 0.25 } },
        { id: 'altar_grand_def', name: 'Divine Fortitude', description: '+80% DEF', iconKey: 'icon_boon_4', effects: { defPct: 0.80 } },
        { id: 'altar_grand_gold', name: 'Divine Greed', description: '×3 gold gain', iconKey: 'icon_boon_10', effects: { goldMult: 3 } },
        { id: 'altar_grand_all', name: 'Divine Harmony', description: '+25% HP · +25% dmg · +25% spell power', iconKey: 'icon_boon_9', effects: { maxHpPct: 0.25, damagePct: 0.25, spellPowerPct: 0.25 } },
    ],
};
/** Tier → base cost multiplier on the mystery-item price. The actual cost at
 *  any given stage is `mysteryPrice × tierMult × 2^repeatIndex` where
 *  repeatIndex starts at 0 and ticks up each time the same tier is picked in
 *  the current visit. */
export const ALTAR_TIER_MULT = {
    modest: 3,
    devout: 10,
    grand: 50,
};
/** Roll one random reward from a tier's pool. */
export function rollAltarReward(tier) {
    const pool = ALTAR_POOLS[tier];
    return pool[Math.floor(Math.random() * pool.length)];
}
/** Stamp an altar reward onto the active-boons list. Same FIFO semantics as
 *  shrine boons (max 2 active, oldest evicted) — altar rewards are just boons
 *  with the curse side omitted, so the engine doesn't care where they came
 *  from. */
export function applyAltarReward(activeBoons, rewardId, maxActive = 2) {
    return applyBoon(activeBoons, rewardId, maxActive);
}
/** Resolve an altar reward's def (covers both BoonDef and AltarRewardDef so
 *  the aggregator sees identical shape). */
export function getAltarRewardDef(id) {
    for (const tier of ['modest', 'devout', 'grand']) {
        const found = ALTAR_POOLS[tier].find((r) => r.id === id);
        if (found)
            return found;
    }
    return undefined;
}
// Extend getBoonDef to also look in altar pools so aggregateBoonEffects
// resolves altar reward ids too. This is exported separately below; callers
// that want combined lookup use this one.
const _origGetBoonDef = getBoonDef;
/** Unified lookup — returns either a shrine boon or an altar reward by id.
 *  Shrine boons are checked first (existing content); altar rewards second. */
export function getBoonOrAltarDef(id) {
    return _origGetBoonDef(id) ?? getAltarRewardDef(id);
}
/** Increment the stack counter on any stacking boon when a kill happens.
 *  Clamps at the per-boon cap. Call once per kill from BattleScene.killEnemy. */
export function onKillTickBoons(activeBoons) {
    for (const ab of activeBoons) {
        const def = getBoonDef(ab.id);
        if (!def?.effects.stackPerKill)
            continue;
        ab.stacks = Math.min(def.effects.stackPerKill.cap, (ab.stacks ?? 0) + 1);
    }
}
//# sourceMappingURL=shrine.js.map